import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';

interface DashboardOverview {
  total_projects: number;
  total_jobs: number;
  jobs_stats: {
    completed: number;
    processing: number;
    failed: number;
    success_rate: number;
  };
  jobs_by_system: Record<string, number>;
}

const SYSTEM_NAMES: Record<string, string> = {
  factsagent: 'Facts Agent',
  topicalmap: 'Topical Map',
  topicalmapv2: 'Topical Map V2',
  cppages: 'CP Pages',
  content: 'Content',
  content_writer: 'Blog Posts',
  contentframework: 'Content Framework',
  contentframeworkv2: 'Content Framework V2',
  cpimages: 'CP Images',
  briefcontent: 'Bulk Brief',
  liveupdate: 'WP Live Update',
  imagegenerator: 'Image Generator',
  serviceareapages: 'Service Area Pages',
  seotools: 'SEO Tools',
  icpagent: 'ICP Agent',
  articleupdate: 'Article Update',
  sop_topical_map: 'SOP Topical Map',
};

function systemName(key: string): string {
  return SYSTEM_NAMES[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export default function AnalyticsDashboardPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/analytics/dashboard/overview');
      setOverview(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <span className="spinner w-10 h-10 mx-auto"></span>
            <p className="mt-4 text-gray-500">Loading analytics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !overview) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 text-lg mb-4">{error}</p>
            <button onClick={loadDashboardData} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const topSystems = Object.entries(overview.jobs_by_system || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">System-wide metrics and performance across all projects</p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-brand-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Projects</p>
                <p className="text-2xl font-bold text-gray-900">{overview.total_projects}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900">{overview.total_jobs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">{overview.jobs_stats.completed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Success Rate</p>
                <p className="text-2xl font-bold text-purple-600">{overview.jobs_stats.success_rate.toFixed(1)}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Job Status Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Job Status Overview</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700">Completed</span>
                    <span className="text-gray-600">{overview.jobs_stats.completed} jobs</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${(overview.jobs_stats.completed / overview.total_jobs) * 100}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700">Processing</span>
                    <span className="text-gray-600">{overview.jobs_stats.processing} jobs</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${(overview.jobs_stats.processing / overview.total_jobs) * 100}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700">Failed</span>
                    <span className="text-gray-600">{overview.jobs_stats.failed} jobs</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500 rounded-full"
                      style={{ width: `${(overview.jobs_stats.failed / overview.total_jobs) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Quick Stats</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-brand-50 rounded-lg">
                  <p className="text-gray-600 text-sm mb-1">Avg per Project</p>
                  <p className="text-2xl font-bold text-brand-600">
                    {overview.total_projects > 0 ? (overview.total_jobs / overview.total_projects).toFixed(1) : 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">jobs</p>
                </div>

                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-gray-600 text-sm mb-1">Completion Rate</p>
                  <p className="text-2xl font-bold text-green-600">
                    {overview.jobs_stats.success_rate.toFixed(0)}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">success</p>
                </div>

                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-gray-600 text-sm mb-1">Failure Rate</p>
                  <p className="text-2xl font-bold text-red-600">
                    {overview.total_jobs > 0 ? ((overview.jobs_stats.failed / overview.total_jobs) * 100).toFixed(1) : 0}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">failed</p>
                </div>

                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-gray-600 text-sm mb-1">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">{overview.jobs_stats.processing}</p>
                  <p className="text-xs text-gray-500 mt-1">running</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Systems */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Top Agent Systems</h3>
              <p className="text-xs text-gray-400 mt-0.5">Most used agents across all projects</p>
            </div>
            <button
              onClick={() => navigate('/agent-monitor')}
              className="text-sm text-brand-600 hover:text-brand-700 font-medium"
            >
              View Live Agents →
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {topSystems.map(([system, count], idx) => {
                const maxCount = topSystems[0]?.[1] || 1;
                const percentage = (count / maxCount) * 100;
                return (
                  <div key={system}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-gray-400">#{idx + 1}</span>
                        <span className="font-medium text-gray-700">{systemName(system)}</span>
                      </div>
                      <span className="text-gray-600 font-medium">{count} jobs</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Platform Integrations Info */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h4 className="font-semibold text-blue-900">Agent Monitor</h4>
            </div>
            <p className="text-sm text-blue-700 mb-3">Real-time agent execution tracking</p>
            <button
              onClick={() => navigate('/agent-monitor')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View Monitor →
            </button>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="font-semibold text-purple-900">System Monitor</h4>
            </div>
            <p className="text-sm text-purple-700 mb-3">System logs and performance metrics</p>
            <button
              onClick={() => navigate('/system-monitor')}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              View Logs →
            </button>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-600 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h4 className="font-semibold text-orange-900">SEO Client Dashboard</h4>
            </div>
            <p className="text-sm text-orange-700 mb-3">GSC, GA4, Ahrefs client scorecards</p>
            <button
              onClick={() => navigate('/seo-dashboard')}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              View SEO Dashboard →
            </button>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-600 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <h4 className="font-semibold text-green-900">Projects</h4>
            </div>
            <p className="text-sm text-green-700 mb-3">Manage all client projects</p>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-green-600 hover:text-green-700 font-medium"
            >
              View Projects →
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
